import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { api, fmtMoney, formatApiError } from "../lib/api";

export default function AdminBulk() {
  const { t } = useTranslation();
  const tr = (k, o) => t(`admin_bulk.${k}`, o);
  const [settings, setSettings] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [reserve, setReserve] = useState(1_000_000);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/bulk/settings"),
      api.get("/projects"),
    ]).then(([s, ps]) => {
      setSettings(s.data);
      setProjects(ps.data || []);
      setSelectedIds(s.data.selected_project_ids || []);
      setReserve(s.data.admin_reserve_usd || 1_000_000);
    }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  }, []);

  const toggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const selectAll = () => setSelectedIds(projects.map((p) => p.id));
  const clearAll = () => setSelectedIds([]);

  const previewPool = useMemo(() => {
    const set = new Set(selectedIds.length ? selectedIds : projects.map((p) => p.id));
    let usd = 0;
    let tons = 0;
    projects.filter((p) => set.has(p.id)).forEach((p) => {
      tons += p.tons_available || 0;
      usd += (p.tons_available || 0) * (p.price_usd_per_ton || 0);
    });
    const available = Math.max(0, usd - Number(reserve || 0));
    return { usd, tons, available };
  }, [selectedIds, projects, reserve]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/bulk/settings", {
        selected_project_ids: selectedIds,
        admin_reserve_usd: Number(reserve) || 0,
        admin_allocations: settings?.admin_allocations || [],
      });
      toast.success("Bulk basket settings saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-6">
        <div className="label-tag mb-4">{tr("tag", "Admin · Bulk basket")}</div>
        <h1 className="font-serif text-5xl tracking-tight">{tr("title", "Curate the bulk pool.")}</h1>
        <p className="text-ink2 mt-3 max-w-2xl text-sm">
          {tr("subtitle", "Select which UCS projects participate in the diversified pro-rata bulk basket. Hold back any portion you want to allocate manually from the trading desk via the admin reserve.")}
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-ink p-6 bg-surface">
          <div className="flex items-center justify-between mb-4">
            <div className="label-tag">{tr("projects_in_basket", "Projects in basket")}</div>
            <div className="flex items-center gap-2 text-[11px] font-mono">
              <button onClick={selectAll} data-testid="select-all" className="underline underline-offset-4 text-ink2 hover:text-ink">{tr("select_all", "Select all")}</button>
              <span className="text-ink2">·</span>
              <button onClick={clearAll} data-testid="clear-all" className="underline underline-offset-4 text-ink2 hover:text-ink">{tr("clear_all", "Clear all")}</button>
            </div>
          </div>
          <div className="space-y-2">
            {projects.map((p) => {
              const isSelected = selectedIds.length === 0 || selectedIds.includes(p.id);
              return (
                <label
                  key={p.id}
                  className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                    isSelected ? "border-ink bg-bg" : "border-line hover:border-ink/50"
                  }`}
                  data-testid={`project-${p.id}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(p.id)}
                    className="h-4 w-4 accent-ink"
                  />
                  <div className="flex-1">
                    <div className="font-serif text-sm">{p.name}</div>
                    <div className="text-[11px] font-mono text-ink2 mt-0.5">
                      {p.region} · {Number(p.tons_available).toLocaleString()} t · ISIN {p.b3_isin || "—"}
                    </div>
                  </div>
                  <div className="text-right mono-num text-sm">
                    {fmtMoney((p.tons_available || 0) * (p.price_usd_per_ton || 0), "USD")}
                  </div>
                </label>
              );
            })}
          </div>
          <div className="text-[11px] font-mono text-ink2 mt-3 leading-relaxed">
            {tr("empty_hint", "Leaving the list empty means \"use all available projects\". Otherwise only the checked projects are allocated by the auto pro-rata.")}
          </div>
        </div>

        <div>
          <div className="border border-ink p-6 bg-surface">
            <div className="label-tag mb-3">{tr("admin_reserve", "Admin reserve")}</div>
            <p className="text-xs text-ink2 mb-3">
              {tr("reserve_hint", "USD value held back from auto-allocation, available for trading-desk manual placement on top of pro-rata.")}
            </p>
            <input
              data-testid="reserve-usd"
              type="number"
              min="0"
              step="50000"
              value={reserve}
              onChange={(e) => setReserve(e.target.value)}
              className="input-field"
            />
            <div className="text-[10px] font-mono text-ink2 mt-1">USD</div>

            <div className="mt-6 pt-5 border-t border-line space-y-2 text-sm">
              <Row k={tr("pool_usd", "Pool total (USD)")} v={fmtMoney(previewPool.usd, "USD")} />
              <Row k={tr("pool_tons", "Pool total (tons)")} v={`${Math.round(previewPool.tons).toLocaleString()} t`} />
              <Row k={tr("reserve", "Reserve")} v={fmtMoney(Number(reserve || 0), "USD")} />
              <Row k={tr("available_auto", "Available for auto")} v={fmtMoney(previewPool.available, "USD")} highlight />
            </div>

            <button
              onClick={save}
              disabled={busy}
              data-testid="save-bulk"
              className="btn-primary w-full mt-5 disabled:opacity-50"
            >
              {busy ? tr("saving", "Saving…") : `${tr("save_settings", "Save settings")} →`}
            </button>
          </div>
          {settings?.updated_at && (
            <div className="text-[11px] font-mono text-ink2 mt-2 text-center">
              {tr("last_updated", "Last updated")} · {new Date(settings.updated_at).toLocaleString()}
            </div>
          )}
        </div>
      </section>

      <div className="h-16" />
    </Layout>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ink2">{k}</span>
      <span className={`mono-num ${highlight ? "text-ink text-lg" : ""}`}>{v}</span>
    </div>
  );
}
